import React, { useEffect, useState } from "react";
import bots from "../botsData";

const initials = (name = "") =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const BotCard = ({ bot }) => {
  const [imgOk, setImgOk] = useState(true);

  return (
    <article className="group relative overflow-hidden apb-surface-glass flex items-center gap-4 rounded-2xl border border-apb-border-soft p-5 transition duration-240 hover:border-apb-accent-cyan/65 hover:apb-glow-cyan hover:-translate-y-1 focus-within:apb-glow-purple">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-apb-accent-cyan to-transparent opacity-70" />
      <div className="pointer-events-none absolute -right-8 -top-8 h-20 w-20 rounded-full bg-apb-accent-purple/18 blur-[18px]" />
      <div className="pointer-events-none absolute -left-10 bottom-0 h-16 w-16 rounded-full bg-apb-accent-cyan/12 blur-[22px]" />

      {imgOk ? (
        <div className="relative h-16 w-16 rounded-full p-[2px] bg-gradient-to-br from-apb-accent-purple via-apb-accent-cyan to-apb-accent-purple">
          <div className="h-full w-full overflow-hidden rounded-full border border-black/20 bg-apb-bg-surface">
            <img
              src={bot.image}
              alt={bot.name}
              loading="lazy"
              className="h-full w-full rounded-full object-cover transition-transform duration-240 group-hover:scale-105"
              style={{ transform: `scale(${bot.imageScale ?? 1})` }}
              onError={() => setImgOk(false)}
            />
          </div>
        </div>
      ) : (
        <div className="h-16 w-16 rounded-full grid place-items-center bg-gradient-to-br from-apb-accent-purple to-apb-accent-cyan p-[2px]">
          <span className="h-full w-full rounded-full grid place-items-center bg-apb-bg-surface text-sm font-bold text-white">
            {initials(bot.name)}
          </span>
        </div>
      )}

      <div className="flex flex-1 flex-col text-left">
        <h3 className="text-xl font-bold text-white apb-glow-text">{bot.name}</h3>
        {bot.voteUrl ? (
          <a
            href={bot.voteUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Vote for ${bot.name} on Top.gg`}
            className="mt-3 inline-flex w-fit items-center gap-2 rounded-lg border border-apb-border-soft px-4 py-2 text-sm font-semibold text-white/95 transition-all duration-240 apb-btn focus-visible:outline-none focus-ring-neo"
          >
            Vote on Top.gg
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="mt-3 inline-block w-fit rounded-lg border border-apb-border-soft bg-apb-bg-surface/60 px-4 py-2 text-sm font-semibold text-apb-text-secondary opacity-90 cursor-not-allowed"
            title="Top.gg vote link coming soon"
          >
            Vote link coming soon
          </button>
        )}
      </div>
    </article>
  );
};

const PatronCard = ({ patron }) => {
  const avatarUrl = patron.avatarUrl || patron.avatar;

  return (
    <article className="group flex items-center gap-3 rounded-xl border border-apb-border-soft apb-surface-glass p-4 text-left transition duration-240 hover:border-apb-accent-cyan/50 hover:apb-glow-cyan">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={`${patron.name} avatar`}
          loading="lazy"
          className="h-12 w-12 rounded-full border border-apb-accent-cyan/35 object-cover transition-transform duration-240 group-hover:scale-105"
        />
      ) : (
        <div className="grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-apb-accent-purple to-apb-accent-cyan font-bold text-white border border-white/20">
          {initials(patron.name)}
        </div>
      )}
      <span className="font-semibold text-white/95">{patron.name}</span>
    </article>
  );
};

const isValidPatron = (patron) =>
  patron && typeof patron.name === "string" && patron.name.trim();

const loadSupporters = async () => {
  const apiUrl = process.env.REACT_APP_PATRONS_API_URL || "/api/patrons";
  const fallbackUrl = `${process.env.PUBLIC_URL}/patrons.json`;

  try {
    const response = await fetch(apiUrl, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("Supporter API unavailable");

    const supporters = await response.json();
    if (!Array.isArray(supporters)) throw new Error("Invalid supporter API response");
    return supporters.filter(isValidPatron);
  } catch {
    try {
      const response = await fetch(fallbackUrl, { headers: { Accept: "application/json" } });
      if (!response.ok) return [];

      const supporters = await response.json();
      return Array.isArray(supporters) ? supporters.filter(isValidPatron) : [];
    } catch {
      return [];
    }
  }
};

const PatronsWall = () => {
  const [patrons, setPatrons] = useState(null);

  useEffect(() => {
    let isCurrent = true;

    loadSupporters().then((supporters) => {
      if (isCurrent) setPatrons(supporters);
    });

    return () => {
      isCurrent = false;
    };
  }, []);

  return (
    <section
      className="relative overflow-hidden apb-surface-glass rounded-2xl p-6 mb-12"
      aria-labelledby="supporters-heading"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-apb-accent-purple to-transparent opacity-70" />
      <h2 id="supporters-heading" className="text-2xl font-bold mb-3 text-white apb-glow-text">
        Thanks to our patrons!
      </h2>
      {patrons === null ? (
        <p className="text-apb-text-secondary">Loading supporter recognition...</p>
      ) : patrons.length === 0 ? (
        <p className="text-apb-text-secondary">
          Want to see your name here? Use the Patreon button above to help shape the future of Afterparty Bot Labs!
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {patrons.map((patron) => (
            <PatronCard
              key={patron.id || `${patron.name}-${patron.memberSince || "supporter"}`}
              patron={patron}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const SupportUs = () => (
  <div className="relative min-h-screen py-16 px-4">
    <div className="pointer-events-none absolute inset-x-0 top-6 left-1/2 h-px w-[80%] -translate-x-1/2 bg-gradient-to-r from-transparent via-apb-accent-cyan to-transparent opacity-70" />
    <div className="pointer-events-none absolute inset-x-0 top-20 left-0 h-full w-full">
      <div className="mx-auto h-full max-w-6xl bg-[linear-gradient(to_right,transparent_0%,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_52px),linear-gradient(to_bottom,transparent_0%,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_52px)] bg-[length:52px_52px,52px_52px] opacity-10" />
    </div>
    <div className="max-w-6xl mx-auto text-center relative z-10">
      <header className="mb-12 apb-surface-glass px-6 py-8 rounded-2xl">
        <p className="inline-flex items-center gap-2 rounded-full border border-apb-border-soft bg-black/20 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-apb-text-secondary">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-apb-accent-cyan apb-glow-cyan" />
          Community-Powered Support
        </p>
        <h1 className="mt-5 text-4xl font-extrabold apb-glow-text text-white md:text-5xl">
          Support Our Bots
        </h1>
        <p className="mt-3 text-lg text-apb-text-secondary max-w-3xl mx-auto">
          Support helps Afterparty Bot Labs keep our Discord bots available, reliable, and improving for the communities that use them.
        </p>
      </header>

      <section className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] mb-12 text-left">
        <article className="relative apb-surface-glass p-6 rounded-2xl border border-apb-border-soft">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-apb-accent-purple to-transparent opacity-70" />
          <h2
            id="patreon-heading"
            className="text-2xl font-bold mb-3 text-white apb-glow-text pl-2 border-l-4 border-apb-accent-purple"
          >
            Become a Patron
          </h2>
          <p className="mb-4 text-apb-text-secondary">
            If you love our bots and want to support development, you can chip in on Patreon. This is donation only: there are no premium slots or perks. Your support helps cover hosting, development, and reliability.
          </p>
          <a
            href="https://patreon.com/AfterpartyClub"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-apb-accent-purple/85 to-apb-accent-cyan/85 px-5 py-2.5 text-sm font-semibold text-white apb-glow-cyan transition duration-240 focus-visible:outline-none focus-ring-neo hover:scale-[1.02] hover:shadow-lg"
          >
            Support Us on Patreon
          </a>
        </article>

        <article className="relative apb-surface-glass p-6 rounded-2xl border border-apb-border-soft">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-apb-accent-cyan to-transparent opacity-70" />
          <h2 className="text-xl font-bold mb-4 text-white apb-glow-text pl-2 border-l-4 border-apb-accent-cyan">
            Patron Support Tiers
          </h2>
          <p className="text-apb-text-secondary">
            We keep this community-first and donation-driven so every level keeps the bots running, improves uptime, and enables rapid feature updates.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs font-semibold text-apb-accent-cyan px-2.5 py-1 rounded-full border border-apb-accent-cyan/35 bg-white/5">
              Donation Only
            </span>
            <span className="text-xs font-semibold text-white/90 px-2.5 py-1 rounded-full border border-apb-accent-purple/35 bg-white/5">
              No Premium Perks
            </span>
            <span className="text-xs font-semibold text-apb-accent-cyan px-2.5 py-1 rounded-full border border-apb-accent-cyan/35 bg-white/5">
              Sustainability Focus
            </span>
          </div>
        </article>
      </section>

      <PatronsWall />

      <section aria-labelledby="vote-heading">
        <h2 id="vote-heading" className="text-2xl text-left font-bold mb-3 text-white apb-glow-text border-l-4 border-apb-accent-purple pl-2">
          Vote for Our Bots
        </h2>
        <p className="text-left text-apb-text-secondary">
          Voting on Top.gg is another simple way to help people discover the projects you enjoy.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {bots.map((bot) => (
            <BotCard key={bot.name} bot={bot} />
          ))}
        </div>
      </section>
    </div>
  </div>
);

export default SupportUs;
